/**
 * Newsletter popup – Swiss Rotors
 *
 * Flow:
 *  1. Opens after a delay (and on exit intent on desktop) unless the visitor
 *     already subscribed or closed it within the last N days.
 *  2. On submit, tags the customer with site, IP-detected country, time zone and
 *     browser language, and posts the Shopify contact form (in a hidden iframe)
 *     so the store receives a notification e-mail with the same data.
 *  3. Then submits the Shopify customer form normally – with spam protection on,
 *     Shopify only accepts regular form posts carrying its hCaptcha token (fetch
 *     posts get "400 invalid parameters"). The page reloads with
 *     ?customer_posted=true and the popup reopens showing the success message.
 */
(() => {
  if (customElements.get('newsletter-popup')) return;

  const STATE_KEY = 'sr-newsletter-popup';
  const PENDING_KEY = 'sr-newsletter-popup-pending';
  const SESSION_KEY = 'sr-newsletter-popup-shown';
  const DAY = 24 * 60 * 60 * 1000;
  const SUBSCRIBED_DAYS = 365 * 5;
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const PREVIEW_PARAM = 'newsletter_popup';

  const storage = {
    get(key, area = 'localStorage') {
      try {
        return JSON.parse(window[area].getItem(key));
      } catch (error) {
        return null;
      }
    },
    set(key, value, area = 'localStorage') {
      try {
        window[area].setItem(key, JSON.stringify(value));
      } catch (error) {
        /* storage blocked – popup still works, it just won't remember */
      }
    },
    remove(key, area = 'localStorage') {
      try {
        window[area].removeItem(key);
      } catch (error) {
        /* ignore */
      }
    },
  };

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const siteHost = () => window.location.hostname.replace(/^www\./, '');
  const isVisible = (element) => {
    if (!element) return false;
    const style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
  };
  // Shopify's cookie consent banner and its preferences dialog.
  const CONSENT_UI_IDS = ['shopify-pc__banner', 'shopify-pc__prefs'];

  class NewsletterPopup extends HTMLElement {
    connectedCallback() {
      // The page loaded inside our hidden submit iframe must stay inert.
      if (/^NewsletterPopup(Form|Notify)-.+-target$/.test(window.name)) return;

      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('.newsletter-popup__form');
      if (!this.dialog || !this.form) return;

      this.notifyForm = this.querySelector('.newsletter-popup__notify');
      this.input = this.form.querySelector('input[type="email"]');
      this.field = this.form.querySelector('.newsletter-popup__field');
      this.tagsInput = this.form.querySelector('[data-np-tags]');
      this.errorEl = this.form.querySelector('[data-np-error]');
      this.submitBtn = this.form.querySelector('[type="submit"]');
      this.submitLabel = this.form.querySelector('[data-np-submit-label]');
      this.sectionId = this.dataset.sectionId;
      this.delay = Math.max(0, Number(this.dataset.delay) || 0);
      this.dismissDays = Math.max(1, Number(this.dataset.dismissDays) || 14);
      this.designMode = Boolean(window.Shopify && window.Shopify.designMode);
      this.loadedAt = Date.now();
      this.timers = [];

      this.form.noValidate = true;

      // Shopify's spam protection (hCaptcha) binds to these forms and, once it has a token,
      // calls form.submit(). It captures form.submit at bind time, so these overrides must be
      // set before binding – they route the protected submit back into our fetch-based flow.
      this.form.submit = () => this.afterCaptcha();
      if (this.notifyForm) this.notifyForm.submit = () => this.sendNotification();

      this.bindEvents();

      if (this.resumeNativeSubmit()) return;

      if (this.designMode) {
        this.setupEditor();
        return;
      }

      if (new URLSearchParams(window.location.search).has(PREVIEW_PARAM)) {
        this.schedule(() => this.open({ force: true }), 600);
        return;
      }

      if (this.dataset.autoOpen === 'false' || !this.canAutoOpen()) return;

      this.schedule(() => this.open(), this.delay * 1000);
      if (this.dataset.exitIntent === 'true') this.setupExitIntent();
    }

    disconnectedCallback() {
      this.timers.forEach(clearTimeout);
      this.timers = [];
      this.teardownExitIntent();
      if (this.onEditorSelect) document.removeEventListener('shopify:section:select', this.onEditorSelect);
      if (this.onEditorDeselect) document.removeEventListener('shopify:section:deselect', this.onEditorDeselect);
      if (this.onKeydown) document.removeEventListener('keydown', this.onKeydown);
      clearTimeout(this.captchaTimer);
      document.documentElement.classList.remove('newsletter-popup-open');
    }

    /* ---------- Visibility rules ---------- */

    canAutoOpen() {
      if (this.dataset.mobile !== 'true' && window.matchMedia('(max-width: 749px)').matches) return false;
      if (storage.get(SESSION_KEY, 'sessionStorage')) return false;

      const state = storage.get(STATE_KEY);
      if (state && state.until && state.until > Date.now()) return false;

      return true;
    }

    schedule(callback, ms) {
      this.timers.push(setTimeout(callback, ms));
    }

    otherOverlayOpen() {
      // Never cover the cookie consent banner – wait until the visitor has answered it.
      if (CONSENT_UI_IDS.some((id) => isVisible(document.getElementById(id)))) return true;

      const body = document.body.classList;
      if (body.contains('overflow-hidden') || body.contains('overflow-hidden-mobile') || body.contains('overflow-hidden-tablet')) {
        return true;
      }
      return Boolean(document.querySelector('dialog[open]:not(.newsletter-popup), cart-drawer.active, .drawer.active'));
    }

    setupExitIntent() {
      if (!window.matchMedia('(pointer: fine)').matches) return;
      this.onMouseOut = (event) => {
        if (event.relatedTarget || event.clientY > 0) return;
        if (Date.now() - this.loadedAt < 3000) return;
        this.open();
      };
      document.addEventListener('mouseout', this.onMouseOut);
    }

    teardownExitIntent() {
      if (!this.onMouseOut) return;
      document.removeEventListener('mouseout', this.onMouseOut);
      this.onMouseOut = null;
    }

    setupEditor() {
      this.onEditorSelect = (event) => {
        if (event.detail.sectionId !== this.sectionId) return;
        window.__newsletterPopupSelected = this.sectionId;
        this.open({ force: true, instant: true });
      };
      this.onEditorDeselect = (event) => {
        if (event.detail.sectionId !== this.sectionId) return;
        window.__newsletterPopupSelected = null;
        this.close({ remember: false });
      };
      document.addEventListener('shopify:section:select', this.onEditorSelect);
      document.addEventListener('shopify:section:deselect', this.onEditorDeselect);

      // The section re-renders on every setting change: keep it open while selected.
      if (window.__newsletterPopupSelected === this.sectionId) this.open({ force: true, instant: true });
    }

    /* ---------- Open / close ---------- */

    async open({ force = false, instant = false } = {}) {
      if (this.dialog.open || this.opening) return;

      if (!force && this.otherOverlayOpen()) {
        this.schedule(() => this.open(), 3000);
        return;
      }

      this.opening = true;
      this.timers.forEach(clearTimeout);
      this.timers = [];
      this.teardownExitIntent();

      await this.prepareImage();

      this.dialog.showModal();
      document.documentElement.classList.add('newsletter-popup-open');
      this.opening = false;

      if (instant || reducedMotion()) {
        this.dialog.classList.add('is-visible');
      } else {
        // In a background tab rAF waits until the tab is visible – so the fade-in plays when it's seen.
        requestAnimationFrame(() => requestAnimationFrame(() => this.dialog.open && this.dialog.classList.add('is-visible')));
      }

      if (!this.designMode) storage.set(SESSION_KEY, true, 'sessionStorage');

      const successTitle = this.querySelector('[data-np-step="success"]:not([hidden]) [data-np-success-title]');
      if (successTitle) {
        successTitle.focus({ preventScroll: true });
      } else if (this.input && window.matchMedia('(pointer: fine)').matches) {
        this.input.focus({ preventScroll: true });
      }

      // Warm up the location lookup and Shopify's spam protection so submitting feels instant.
      this.getLocation();
      this.ensureCaptcha(this.form);
      if (this.notifyForm) {
        this.ensureCaptcha(this.notifyForm);
        this.ensureFrame(this.notifyForm);
      }
    }

    /**
     * hCaptcha may need to show a challenge. It renders outside the popup, where a modal
     * dialog would cover it and make it unclickable – so switch to a non-modal dialog
     * (same look: the dim layer is the dialog itself) before running the captcha.
     */
    makeNonModal() {
      if (!this.dialog.open || !this.dialog.matches(':modal')) return;
      const active = document.activeElement;
      // Swap without transitions, otherwise the panel restarts its fade-in from opacity 0.
      this.dialog.classList.add('is-switching');
      this.dialog.close();
      this.dialog.show();
      void this.dialog.offsetWidth;
      if (active && this.dialog.contains(active)) active.focus({ preventScroll: true });
      setTimeout(() => this.dialog.classList.remove('is-switching'), 50);
    }

    /** Tokens are single-use: reset the form's hCaptcha widget before it is executed again. */
    resetCaptcha(form) {
      try {
        const frame = form && form.querySelector('iframe[data-hcaptcha-widget-id]');
        if (frame && window.hcaptcha) window.hcaptcha.reset(frame.dataset.hcaptchaWidgetId);
      } catch (error) {
        /* ignore */
      }
    }

    /** Resolves true once Shopify's hCaptcha is bound to the form, false when the store doesn't use it. */
    ensureCaptcha(form) {
      if (!form) return Promise.resolve(false);
      if (form.hasAttribute('data-hcaptcha-bound')) return Promise.resolve(true);
      const protect = window.Shopify && window.Shopify.captcha && window.Shopify.captcha.protect;
      if (typeof protect !== 'function') return Promise.resolve(false);

      if (!form.npCaptchaReady) {
        form.npCaptchaReady = new Promise((resolve) => {
          const done = () => resolve(form.hasAttribute('data-hcaptcha-bound'));
          try {
            protect(form, done);
          } catch (error) {
            resolve(false);
          }
          setTimeout(done, 10000);
        });
      }
      return form.npCaptchaReady;
    }

    async close({ remember = true } = {}) {
      if (!this.dialog.open || this.closing) return;
      this.closing = true;

      this.dialog.classList.remove('is-visible');
      await wait(reducedMotion() ? 0 : 260);

      this.dialog.close(); // the 'close' listener resets this.closing
      document.documentElement.classList.remove('newsletter-popup-open');

      if (remember && !this.designMode && !this.subscribed) {
        storage.set(STATE_KEY, { state: 'dismissed', until: Date.now() + this.dismissDays * DAY });
      }
    }

    async prepareImage() {
      const images = [...this.querySelectorAll('.newsletter-popup__image, .newsletter-popup__logo')];
      const pending = images.map((image) => {
        image.loading = 'eager';
        if (image.complete || typeof image.decode !== 'function') return Promise.resolve();
        return image.decode().catch(() => {});
      });
      await Promise.race([Promise.all(pending), wait(1200)]);
    }

    bindEvents() {
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });

      // Safety net: the browser may close the dialog on its own (e.g. repeated Esc).
      this.dialog.addEventListener('close', () => {
        if (this.dialog.open) return; // re-opened as non-modal (see makeNonModal)
        this.dialog.classList.remove('is-visible');
        document.documentElement.classList.remove('newsletter-popup-open');
        if (this.closing) {
          this.closing = false;
          return;
        }
        if (!this.designMode && !this.subscribed) {
          storage.set(STATE_KEY, { state: 'dismissed', until: Date.now() + this.dismissDays * DAY });
        }
      });

      // Close on any click outside the panel (the dialog element is the full-screen layer).
      this.dialog.addEventListener('pointerdown', (event) => {
        this.pressedOutside = event.target === this.dialog;
      });
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog && this.pressedOutside !== false) this.close();
        this.pressedOutside = null;
      });

      this.querySelectorAll('[data-np-close]').forEach((button) => {
        button.addEventListener('click', () => this.close());
      });

      // Esc for the non-modal state (a modal dialog gets the native 'cancel' event instead).
      this.onKeydown = (event) => {
        if (event.key !== 'Escape' || !this.dialog.open || this.dialog.matches(':modal')) return;
        this.close();
      };
      document.addEventListener('keydown', this.onKeydown);

      this.form.addEventListener('submit', (event) => this.onSubmit(event));
      this.input?.addEventListener('input', () => this.clearError());
    }

    /* ---------- Location ---------- */

    getLocation() {
      if (this.locationPromise) return this.locationPromise;

      let timezone = '';
      try {
        timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
      } catch (error) {
        /* ignore */
      }

      const fallback = {
        country: (window.Shopify && window.Shopify.country) || '',
        countryName: '',
        source: 'market',
        timezone,
        language: navigator.language || '',
      };

      const lookup = fetch('/browsing_context_suggestions.json?country[enabled]=true', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      })
        .then((response) => (response.ok ? response.json() : null))
        .then((data) => {
          const detected = data && data.detected_values;
          const country = detected && detected.country;
          if (!country || !country.handle) return fallback;
          return {
            ...fallback,
            country: country.handle,
            countryName: country.name || detected.country_name || '',
            source: 'ip',
          };
        })
        .catch(() => fallback);

      this.locationPromise = Promise.race([lookup, wait(2500).then(() => fallback)]);
      return this.locationPromise;
    }

    buildTags(location) {
      // Shopify rejects storefront sign-ups ("invalid parameters") for some tag characters,
      // so tags are kept to letters, digits and hyphens: country-PL, timezone-Europe-Warsaw…
      const clean = (value) =>
        String(value)
          .replace(/[^A-Za-z0-9_-]+/g, '-')
          .replace(/-{2,}/g, '-')
          .replace(/^-|-$/g, '');

      const tags = (this.dataset.tags || 'newsletter').split(',').map(clean).filter(Boolean);

      tags.push(`site-${clean(siteHost())}`);
      if (location.country) tags.push(`country-${clean(location.country)}`);
      if (location.timezone) tags.push(`timezone-${clean(location.timezone)}`);
      if (location.language) tags.push(`lang-${clean(location.language)}`);

      return [...new Set(tags)].join(', ');
    }

    /* ---------- Submit ---------- */

    async onSubmit(event) {
      // We drive the whole flow (including Shopify's captcha) ourselves.
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.busy) return;

      const email = (this.input.value || '').trim();
      if (!EMAIL_RE.test(email)) {
        this.showError(this.dataset.errorInvalid);
        this.input.focus();
        return;
      }

      this.setBusy(true);
      this.clearError();
      this.input.value = email;
      this.location = await this.getLocation();
      if (this.tagsInput) this.tagsInput.value = this.buildTags(this.location);

      // hCaptcha may need to show a challenge, which a modal dialog would cover.
      this.makeNonModal();

      // 1. Notification e-mail (Shopify contact form) – sent in the background, never blocks for long.
      await this.notify(email, this.location);

      // 2. The sign-up itself: a regular Shopify form submit (the only way Shopify accepts it with
      //    spam protection on). The page reloads with ?customer_posted=true and the popup reopens
      //    showing the success message (see resumeNativeSubmit).
      storage.set(
        PENDING_KEY,
        { section: this.sectionId, at: Date.now(), email, scrollY: window.scrollY },
        'sessionStorage'
      );
      if (await this.ensureCaptcha(this.form)) {
        clearTimeout(this.captchaTimer);
        this.captchaTimer = setTimeout(() => {
          if (!this.busy) return;
          storage.remove(PENDING_KEY, 'sessionStorage');
          this.setBusy(false);
          this.showError(this.dataset.errorGeneric);
        }, 60000);
        this.form.submit(); // Shopify adds an hCaptcha token, then calls afterCaptcha()
      } else {
        this.afterCaptcha();
      }
    }

    /** Called by Shopify's captcha once the form carries a token (or directly when the store has none). */
    afterCaptcha() {
      clearTimeout(this.captchaTimer);
      HTMLFormElement.prototype.submit.call(this.form);
    }

    /** Posts a form natively into its hidden iframe; resolves once the frame has loaded (or on timeout). */
    submitInFrame(form, timeout = 25000) {
      const frame = this.ensureFrame(form);
      return new Promise((resolve) => {
        let settled = false;
        const finish = (result) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          resolve(result);
        };
        const timer = setTimeout(() => finish({ timeout: true }), timeout);

        frame.addEventListener(
          'load',
          () => {
            let url = '';
            let doc = null;
            try {
              url = frame.contentWindow.location.href;
              doc = frame.contentDocument;
            } catch (error) {
              /* cross-origin (should not happen) */
            }
            finish({ url, doc });
          },
          { once: true }
        );

        const previousTarget = form.getAttribute('target');
        form.setAttribute('target', frame.name);
        HTMLFormElement.prototype.submit.call(form);
        if (previousTarget) form.setAttribute('target', previousTarget);
        else form.removeAttribute('target');
      });
    }

    ensureFrame(form) {
      const name = `${form.id}-target`;
      let frame = document.querySelector(`iframe[name="${CSS.escape(name)}"]`);
      if (!frame) {
        frame = document.createElement('iframe');
        frame.name = name;
        frame.title = 'Newsletter sign-up';
        frame.tabIndex = -1;
        frame.setAttribute('aria-hidden', 'true');
        frame.style.cssText = 'position:absolute;width:0;height:0;border:0;visibility:hidden;';
        document.body.appendChild(frame);
      }
      return frame;
    }

    setFields(form, fields) {
      Object.entries(fields).forEach(([name, value]) => {
        let input = form.elements[name];
        if (!input) {
          input = Object.assign(document.createElement('input'), { type: 'hidden', name });
          form.appendChild(input);
        }
        input.value = value;
      });
    }

    /** Sends the notification e-mail through the Shopify contact form. Never blocks for long. */
    async notify(email, location) {
      if (!this.notifyForm || this.notified) return;
      this.notified = true;

      const country = location.countryName ? `${location.countryName} (${location.country})` : location.country || 'Unknown';
      // Plain field names and ASCII values only.
      this.setFields(this.notifyForm, {
        'contact[email]': email,
        'contact[Source]': `Newsletter popup - ${siteHost()}`,
        'contact[Country]': `${country}${location.source === 'ip' ? ', detected from IP' : ''}`,
        'contact[Timezone]': location.timezone || 'Unknown',
        'contact[Language]': location.language || 'Unknown',
        'contact[Page]': `${siteHost()}${window.location.pathname}`,
        'contact[Date]': new Date().toISOString(),
      });

      const sent = new Promise((resolve) => {
        this.resolveNotification = resolve;
      });

      if (await this.ensureCaptcha(this.notifyForm)) {
        this.makeNonModal();
        this.notifyForm.submit(); // Shopify adds an hCaptcha token, then calls sendNotification()
      } else {
        this.sendNotification();
      }

      await Promise.race([sent, wait(12000)]);
    }

    async sendNotification() {
      // Shopify processes the post; the response itself is blocked from rendering in the frame
      // (X-Frame-Options: DENY), so this is fire-and-forget – we only wait for the frame to settle.
      await this.submitInFrame(this.notifyForm, 8000);
      if (this.resolveNotification) this.resolveNotification();
    }

    /** After a native (fallback) submit the page reloads – show the outcome in the popup. */
    resumeNativeSubmit() {
      const pending = storage.get(PENDING_KEY, 'sessionStorage');
      if (!pending || pending.section !== this.sectionId) return false;
      storage.remove(PENDING_KEY, 'sessionStorage');

      // Shopify sends the visitor back to the top of the page – put them where they were.
      if (typeof pending.scrollY === 'number') {
        requestAnimationFrame(() => window.scrollTo(0, pending.scrollY));
      }

      if (new URLSearchParams(window.location.search).get('customer_posted') === 'true') {
        this.showSuccess({ focus: false, email: pending.email });
        this.open({ force: true, instant: true }); // focuses the success heading
        return true;
      }

      const serverError = this.querySelector('[data-np-server-error]');
      if (serverError) {
        this.open({ force: true, instant: true });
        this.showError(serverError.textContent.replace(/\s+/g, ' ').trim());
        return true;
      }

      return false;
    }

    /* ---------- UI states ---------- */

    setBusy(busy) {
      this.busy = busy;
      this.submitBtn.disabled = busy;
      this.submitBtn.setAttribute('aria-busy', String(busy));
      if (!this.submitLabel) return;
      if (busy) {
        this.idleLabel = this.idleLabel || this.submitLabel.textContent;
        this.submitLabel.textContent = this.dataset.busyLabel || this.idleLabel;
      } else if (this.idleLabel) {
        this.submitLabel.textContent = this.idleLabel;
      }
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message || this.dataset.errorGeneric || '';
      this.field?.classList.add('is-invalid');
      this.input?.setAttribute('aria-invalid', 'true');
    }

    clearError() {
      if (!this.errorEl || !this.errorEl.textContent) return;
      this.errorEl.textContent = '';
      this.field?.classList.remove('is-invalid');
      this.input?.removeAttribute('aria-invalid');
    }

    showSuccess({ focus = true, email = '' } = {}) {
      this.subscribed = true;
      this.setBusy(false);
      storage.set(STATE_KEY, { state: 'subscribed', until: Date.now() + SUBSCRIBED_DAYS * DAY });

      const formStep = this.querySelector('[data-np-step="form"]');
      const successStep = this.querySelector('[data-np-step="success"]');
      if (formStep) formStep.hidden = true;
      if (successStep) successStep.hidden = false;

      // Show back the address that was subscribed, so a typo is visible.
      const address = email || (this.input && this.input.value.trim());
      const subscribedAs = this.querySelector('[data-np-subscribed-as]');
      if (subscribedAs && address) {
        subscribedAs.textContent = `${this.dataset.subscribedAs || ''} `;
        const strong = document.createElement('strong');
        strong.textContent = address;
        subscribedAs.appendChild(strong);
      }

      // The dialog is now named and described by the success step.
      const titleId = `${this.dialog.id}-success-title`;
      const textId = `${this.dialog.id}-success-text`;
      if (document.getElementById(titleId)) this.dialog.setAttribute('aria-labelledby', titleId);
      if (document.getElementById(textId)) this.dialog.setAttribute('aria-describedby', textId);
      else this.dialog.removeAttribute('aria-describedby');

      if (focus) this.querySelector('[data-np-success-title]')?.focus({ preventScroll: true });

      document.dispatchEvent(new CustomEvent('newsletter-popup:subscribed', { detail: { sectionId: this.sectionId } }));
      if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: 'newsletter_signup', signup_method: 'popup' });
    }
  }

  customElements.define('newsletter-popup', NewsletterPopup);
})();
